import _ from 'lodash';
import * as ace from 'ace-builds';
import themeXCodeUrl from 'ace-builds/src-noconflict/theme-xcode.js?url';
import monokaiCodeUrl from 'ace-builds/src-noconflict/theme-monokai.js?url';
import modePythonUrl from 'ace-builds/src-noconflict/mode-python?url';
import modeC_cppUrl from 'ace-builds/src-noconflict/mode-c_cpp?url';

ace.config.setModuleUrl('ace/mode/c_cpp', modeC_cppUrl);
ace.config.setModuleUrl('ace/mode/python', modePythonUrl);
ace.config.setModuleUrl('ace/theme/xcode', themeXCodeUrl);
ace.config.setModuleUrl('ace/theme/monokai', monokaiCodeUrl);

const modes = {
  py: 'ace/mode/python',
  cpp: 'ace/mode/c_cpp',
};

let files;
let editor;
let currentTab;
let editorSessions = [];
let tabs;
let storage;
let explorer = [];
const editorInput = document.getElementById('editorInput');
const editorOutput = document.getElementById('editorOutput');

document.addEventListener('DOMContentLoaded', async () => {
  editorInput.addEventListener('input', () => {
    if (editorInput.value == '') {
      editorInput.dir = document.dir;
      editorInput.classList.add('ltr:font-Nunito');
      editorInput.classList.remove('font-Consolas');
    } else {
      editorInput.dir = 'ltr';
      editorInput.classList.remove('ltr:font-Nunito');
      editorInput.classList.add('font-Consolas');
    }
  });
});

let tabsUpdatedEvent = new Event('tabs_updated');
const loadFiles = (filesJson, storageId) => {
  initializeExplorer(filesJson);
  storage = storageId;
  files = filesJson;
  currentTab = files.files.find((file) => file.id === files.currentTab);
  tabs = initializeTabs();
  let editorSession = initializeEditor();
  editorSession.on('change', (delta) => {
    _.debounce(saveEditorContent, 10)();
  });
  Alpine.data('tabs', () => ({
    data: tabs,
    current: currentTab,
    init() {
      window.addEventListener('tabs_updated', () => {
        this.data = initializeTabs();
        this.changeTab(currentTab);
        localStorage.setItem(storage, JSON.stringify(files));
      });
    },
    changeTab(tab) {
      if (this.current !== tab) {
        this.current = tab;
        files.currentTab = this.current.id;
        currentTab = this.current;
        editorSession = setEditorSession(tab.id);
        localStorage.setItem(storage, JSON.stringify(files));
      }
    },
    closeTab(tab) {
      if (this.data.length > 1) {
        files.tabs = files.tabs.filter((id) => {
          return id !== tab.id;
        });
        this.data = initializeTabs();
        if (this.current === tab) {
          this.changeTab(this.data[0]);
        }
        localStorage.setItem(storage, JSON.stringify(files));
      }
    },
  }));
  Alpine.data('explorer', () => ({
    isShown: false,
    explorer: explorer[0].children,
    render: render,
    openFile(id) {
      let file = files.files.find((file) => file.id === id);
      if (!files.tabs.includes(file.id)) {
        files.tabs.push(file.id);
      }
      currentTab = file;
      let tabs = files.files
        .filter((file) => {
          return files.tabs.includes(file.id);
        })
        .sort((a, b) => files.tabs.indexOf(b.id) - files.tabs.indexOf(a.id));
      createTabsSession(tabs);
      setEditorSession(file.id);
      window.dispatchEvent(tabsUpdatedEvent);
      this.hideExplorer();
    },
    toggleHidden(el, childrenEl) {
      el.classList.toggle('active');
      childrenEl.classList.toggle('hidden');
    },
    toggleExplorer() {
      this.isShown = !this.isShown;
    },
    showExplorer() {
      this.isShown = true;
    },
    hideExplorer() {
      this.isShown = false;
      editor.focus();
    },
  }));
  let sendBtn = document.getElementById('sendBtn');
  if (sendBtn) {
    sendBtn.onclick = () => {
      localStorage.setItem(storage, JSON.stringify(files));
      Livewire.dispatch('submitCode', {
        files: JSON.parse(localStorage.getItem(storage))['files'],
      });
    };
  }
};

function saveEditorContent() {
  let currentFile = files.files.find((file) => file.id === files.currentTab);
  files.files.find((file) => file.id === files.currentTab).content =
    editor.session.getValue();
  saveFilesToLocalStorage();
}

function saveFilesToLocalStorage() {
  localStorage.setItem(storage, JSON.stringify(files));
}

const initializeEditor = () => {
  const editorElement = document.getElementById('editor');
  if (editorElement) {
    editor = ace.edit(document.getElementById('editor'), {
      useWorker: false,
      mode: modes[currentTab.mode],
      theme: document.body.classList.contains('dark')
        ? 'ace/theme/monokai'
        : 'ace/theme/xcode',
      minLines: 10,
      fontSize: 14,
      enableAutoIndent: true,
    });
    editor.setOptions({
      autoScrollEditorIntoView: true,
      showPrintMargin: false,
    });
    document.addEventListener('setTheme', function (e) {
      editor.setTheme(
        e.detail === 'dark' ? 'ace/theme/monokai' : 'ace/theme/xcode',
      );
    });
    document.addEventListener('setAceCode', function (e) {
      editor.setValue(e.detail);
    });
    setEditorSession(currentTab.id);
  }
  return editor.session;
};

function createTabsSession(tabs) {
  tabs.forEach((tab) => {
    editorSessions[tab.id] = ace.createEditSession(
      tab.content,
      modes[tab.mode],
    );
  });
}

const setEditorSession = (id) => {
  if (!editorSessions[id]) {
    let tab = files.files.find((file) => file.id === id);
    editorSessions[id] = ace.createEditSession(tab.content, modes[tab.mode]);
  }
  if (editor) {
    editor.setSession(editorSessions[id]);
  }
  editor.session.on('change', (delta) => {
    _.debounce(saveEditorContent, 10)();
  });
  editor.focus();
  editor.navigateFileEnd();
  return editor.session;
};

const initializeTabs = () => {
  let tabs = files.files.filter((file) => {
    return files.tabs.includes(file.id);
  });
  createTabsSession(tabs);
  return tabs;
};

function initializeExplorer(json) {
  let availableFiles = json.files;
  let availableDirectories = json.directories;
  function fillDirectory(parent, id) {
    availableDirectories
      .filter((dir) => dir.parentId === id)
      .sort((a, b) => {
        if (a.name < b.name) {
          return 1;
        }
        if (a.name > b.name) {
          return -1;
        }
        return 0;
      })
      .forEach((dir) => {
        parent.children.unshift({
          id: dir.id,
          name: dir.name,
          parentId: parent.id,
          children: availableFiles
            .filter((file) => file.dirId === dir.id)
            .sort((a, b) => {
              if (a.name < b.name) {
                return 1;
              }
              if (a.name > b.name) {
                return -1;
              }
              return 0;
            }),
        });
        fillDirectory(
          parent.children.find((item) => item.id === dir.id),
          dir.id,
        );
      });
  }
  explorer = [
    {
      id: 'root',
      name: 'root',
      children: availableFiles
        .filter((file) => file.dirId === 'root')
        .sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        }),
    },
  ];
  fillDirectory(explorer[0], 'root');
}

function render(obj, key) {
  let isDirectory = obj.children;

  let html;
  if (isDirectory) {
    let isInRoot = obj.parentId === 'root';
    html = `<button x-transition x-ref='${obj.id.replaceAll('/', '')}' @click='toggleHidden($refs.${obj.id.replaceAll(
      '/',
      '',
    )},$refs.${obj.id.replaceAll(
      '/',
      '',
    )}_children)' class='explorer_directory_button h-9 flex items-center justify-end gap-4'>
                            <span :class="{'text-secondary-600':${!isInRoot}}" class='font-Nunito font-[700] text-sm leading-7 text-fg-default'>
                                ${obj.name}
                            </span>
                            <div class='flex items-center gap-2'>
                                <svg :class="{'hidden':${!isInRoot}}" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"
                                     fill="none">
                                    <path d="M7.54175 1.66689C8.31758 1.65869 9.04175 2.01703 9.51675 2.62536L10.2501 3.60036C10.4834 3.89203 10.8334 4.06703 11.2084 4.06703H13.7667C16.8417 4.06703 18.3417 5.70869 18.3334 9.07536V13.1337C18.3334 16.3504 16.3501 18.3337 13.1251 18.3337H6.86675C3.65841 18.3337 1.66675 16.3504 1.66675 13.1254V6.86703C1.66675 3.41703 3.20008 1.66689 6.22508 1.66689H7.54175ZM13.8584 11.492H6.14175C5.79175 11.492 5.51675 11.767 5.51675 12.117C5.51675 12.4587 5.79175 12.742 6.14175 12.742H13.8584C14.2001 12.742 14.4751 12.4587 14.4751 12.117C14.4751 11.767 14.2001 11.492 13.8584 11.492Z"
                                          fill="#333333" />
                                </svg>
                                <svg :class="{'hidden':${isInRoot}}" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path opacity="0.4" fill-rule="evenodd" clip-rule="evenodd" d="M13.6624 4.46958H11.4524C11.0241 4.46792 10.6149 4.26375 10.3583 3.92208L9.64492 2.97292C9.23075 2.42292 8.57325 2.09375 7.88575 2.09375H6.32325C3.46992 2.09375 2.08325 3.68292 2.08325 6.95125V13.0446C2.08325 16.0879 3.90409 17.9054 6.95409 17.9054H13.0341C16.0774 17.9054 17.8949 16.0879 17.8949 13.0471L17.9166 9.16542C17.9166 5.96125 16.5649 4.46958 13.6624 4.46958Z" fill="#333333"/>
                                  <path fill-rule="evenodd" clip-rule="evenodd" d="M13.7324 12.6787H6.23657C5.89157 12.6787 5.61157 12.3987 5.61157 12.0537C5.61157 11.7087 5.89157 11.4287 6.23657 11.4287H13.7324C14.0774 11.4287 14.3574 11.7087 14.3574 12.0537C14.3574 12.3987 14.0774 12.6787 13.7324 12.6787Z" fill="#333333"/>
                                </svg>
                                <svg class='chevron' x-transition :class="{'hidden':${isInRoot}}" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path d="M14 8C14 8 11.632 12 10 12C8.36857 12 6 8 6 8" stroke="#8B8B8B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <svg class='chevron' x-transition :class="{'hidden':${!isInRoot}}" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path d="M14 8C14 8 11.632 12 10 12C8.36857 12 6 8 6 8" stroke="#333333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <x-icon-explorer-chevron />
                            </div>
                        </button>`;
    if (obj.children) {
      html += `<div class='flex hidden flex-col justify-end items-end ml-7' x-transition x-ref='${obj.id.replaceAll('/', '')}_children'>
                            <template x-for='(item,i) in item.children' class=''>
                            <div class='flex justify-end items-end'>
                                <div  x-html="render(item,i)" class='flex flex-col justify-end items-end'>
                                 
                                </div>
                            </div>
                            </template>
                        </div>`;
    }
  } else {
    let isInRoot = obj.dirId === 'root';
    html = `<button @click='openFile("${
      obj.id
    }")' class='h-9 flex items-center justify-end gap-4 pl-2'>
                            <span :class="{'text-secondary-600':${!isInRoot}}" class='font-Nunito font-[700] text-sm leading-7 text-fg-default'>
                                ${obj.name}
                            </span>
                            <div class='flex items-center gap-2'>
                                <svg class="*:fill-fg-default" :class="{'hidden':${isInRoot}}" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path opacity="0.4" fill-rule="evenodd" clip-rule="evenodd" d="M16.5417 7.58008C16.7425 7.58008 16.9033 7.74258 16.9033 7.94508V14.4751C16.9033 16.4892 15.2875 18.1217 13.285 18.1217H6.89251C4.80168 18.1217 3.09668 16.4084 3.09668 14.2967V5.54175C3.09668 3.52758 4.72168 1.87842 6.72335 1.87842H11.0175C11.2267 1.87842 11.3875 2.04925 11.3875 2.25258V4.86758C11.3875 6.35342 12.6017 7.57175 14.0733 7.58008H16.5417Z" fill="#333333"/>
                                  <path d="M14.18 6.39296C14.7409 6.39879 15.52 6.40129 16.1808 6.39879C16.5192 6.39796 16.6917 5.98963 16.4567 5.74379C16.0325 5.29796 15.4417 4.67713 14.8484 4.05379C14.2525 3.42879 13.6542 2.79963 13.2192 2.34296C12.9792 2.09046 12.5583 2.26379 12.5583 2.61379V4.75463C12.5583 5.65296 13.2909 6.39296 14.18 6.39296Z" fill="#333333"/>
                                </svg>
                                <svg class="*:fill-fg-default" :class="{'hidden':${!isInRoot}}" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <path fill-rule="evenodd" clip-rule="evenodd" d="M16.5417 7.58008C16.7425 7.58008 16.9033 7.74258 16.9033 7.94508V14.4751C16.9033 16.4892 15.2875 18.1217 13.285 18.1217H6.89251C4.80168 18.1217 3.09668 16.4084 3.09668 14.2967V5.54175C3.09668 3.52758 4.72168 1.87842 6.72335 1.87842H11.0175C11.2267 1.87842 11.3875 2.04925 11.3875 2.25258V4.86758C11.3875 6.35342 12.6017 7.57175 14.0733 7.58008H16.5417Z" fill="#333333"/>
                                  <path d="M14.18 6.39296C14.7409 6.39879 15.52 6.40129 16.1808 6.39879C16.5192 6.39796 16.6917 5.98963 16.4567 5.74379C16.0325 5.29796 15.4417 4.67713 14.8484 4.05379C14.2525 3.42879 13.6542 2.79963 13.2192 2.34296C12.9792 2.09046 12.5583 2.26379 12.5583 2.61379V4.75463C12.5583 5.65296 13.2909 6.39296 14.18 6.39296Z" fill="#333333"/>
                                </svg>
                            </div>
                        </button>`;
  }
  return html;
}

window.loadFiles = loadFiles;

function setCookie(cname, cvalue, exseconds) {
  const d = new Date();
  d.setTime(d.getTime() + exseconds * 1000);
  let expires = 'expires=' + d.toUTCString();
  document.cookie = cname + '=' + cvalue + ';' + expires + ';path=/';
}

const initializeDivider = () => {
  const topSectionMaxHeightPercent = 60;
  const wrapperSelector = '#editorWrapper';
  const dividerSelector = '#editorDivider';

  const mainSectionMinHeightInPercent = 25;

  const divider = document.querySelector(
    wrapperSelector + ' ' + dividerSelector,
  );

  const topSection = divider.previousElementSibling;
  const bottomSection = divider.nextElementSibling;

  let y = 0;

  let prevSiblingHeight = 0;

  const getClientY = (e) => {
    return e.touches ? e.touches[0].clientY : e.clientY;
  };

  const mouseDownHandler = function (e) {
    // Get the current mouse position
    y = getClientY(e);
    const rect = topSection.getBoundingClientRect();
    prevSiblingHeight = rect.height;

    // Attach the listeners to `document`
    document.addEventListener('mousemove', mouseMoveHandler);
    document.addEventListener('touchmove', mouseMoveHandler);
    document.addEventListener('mouseup', mouseUpHandler);
    document.addEventListener('touchend', mouseUpHandler);
  };
  const mouseMoveHandler = function (e) {
    // How far the mouse has been moved
    const dy = getClientY(e) - y;

    let h =
      ((prevSiblingHeight + dy) * 100) /
      divider.parentNode.getBoundingClientRect().height;
    h = h >= mainSectionMinHeightInPercent ? h : mainSectionMinHeightInPercent;
    topSection.style.height = Math.min(h, topSectionMaxHeightPercent) + '%';
    const cursor = 'row-resize';
    divider.style.cursor = cursor;
    document.body.style.cursor = cursor;

    topSection.style.userSelect = 'none';
    topSection.style.pointerEvents = 'none';

    bottomSection.style.userSelect = 'none';
    bottomSection.style.pointerEvents = 'none';
  };

  const mouseUpHandler = function () {
    divider.style.removeProperty('cursor');
    document.body.style.removeProperty('cursor');

    topSection.style.removeProperty('user-select');
    topSection.style.removeProperty('pointer-events');

    bottomSection.style.removeProperty('user-select');
    bottomSection.style.removeProperty('pointer-events');

    // Remove the handlers of mousemove and mouseup
    document.removeEventListener('mousemove', mouseMoveHandler);
    document.removeEventListener('touchmove', mouseMoveHandler);
    document.removeEventListener('mouseup', mouseUpHandler);
    document.removeEventListener('touched', mouseUpHandler);
    setCookie('editor-height', topSection.style.height, 365 * 24 * 60 * 60);
  };

  // Attach the handler
  divider.addEventListener('mousedown', mouseDownHandler);
  divider.addEventListener('touchstart', mouseDownHandler);
};

const initializeIoTabs = () => {
  const tabsContentsClassnamePrefix = 'editor-io-tab-content-';
  const tabTriggersClassnamePrefix = 'editor-io-tab-trigger-';

  let triggers = document.querySelectorAll("[class^='editor-io-tab-trigger-']");
  let contents = document.querySelectorAll("[class^='editor-io-tab-content-']");

  for (let i = 0; i < triggers.length; i++) {
    let trigger = triggers[i];
    trigger.onclick = () => {
      for (let trigger of triggers) {
        trigger.classList.remove('active');
      }
      trigger.classList.add('active');
      for (let content of contents) {
        content.classList.add('hidden');
      }
      contents[i].classList.remove('hidden');
    };
  }
};

const load = () => {
  initializeDivider();
  initializeIoTabs();
};

document.addEventListener('livewire:initialized', () => {
  load();
  Livewire.hook('component.init', ({ component, cleanup }) => {
    if (component.name === 'platform.editor.editor') {
      load();
    }
  });
});
